import React from "react";
import { useNavigate } from "react-router-dom";
import {
  FaApple,
  FaArrowRight,
  FaEnvelope,
  FaFacebookF,
  FaGooglePlay,
  FaInstagram,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaTruck,
  FaWhatsapp,
} from "react-icons/fa";

const COMPANY_NAME = "KKISANFRESH DAILY PRIVATE LIMITED";

const Footer = () => {
  const navigate = useNavigate();
  const footerLogoSrc = "/k_kisanlogo.png";

  const quickLinks = [
    { label: "Products", path: "/products" },
    { label: "About Us", path: "/about" },
    { label: "Contact", path: "/contact" },
    { label: "Privacy Policy", path: "/privacy-policy" },
    { label: "Terms & Conditions", path: "/policy/terms" },
    { label: "Refund Policy", path: "/policy/refund" },
  ];

  const socials = [
    {
      label: "Facebook",
      href: "https://www.facebook.com/profile.php?id=61590549803646",
      Icon: FaFacebookF,
      badgeBg: "bg-[#1877F2]",
    },
    {
      label: "Instagram",
      href: "https://www.instagram.com/",
      Icon: FaInstagram,
      badgeBg: "bg-gradient-to-tr from-amber-500 via-rose-500 to-purple-600",
    },
    {
      label: "WhatsApp",
      href: "https://wa.me/919699664866",
      Icon: FaWhatsapp,
      badgeBg: "bg-[#25D366]",
    },
  ];

  const contacts = [
    { label: "+91 9699664866", href: "tel:+919699664866", Icon: FaPhoneAlt },
    {
      label: "info@kisandailyfresh.in",
      href: "mailto:info@kisandailyfresh.in",
      Icon: FaEnvelope,
    },
    { label: "Pune Maharashtra", Icon: FaMapMarkerAlt },
  ];

  return (
    <footer className="bg-gray-950 text-white">
      <div className="bg-gradient-to-r from-primary-700 to-primary-600">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-3 sm:flex-row">
            <p className="flex items-center gap-2 text-sm font-medium text-white">
              <FaTruck className="h-4 w-4" aria-hidden="true" />
              <span>
                Free delivery on orders above{" "}
                <span className="font-bold">Rs 500</span> - Order now!
              </span>
            </p>
            <button
              onClick={() => navigate("/products")}
              className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-white px-5 py-2 text-sm font-bold text-primary-700 shadow transition hover:bg-primary-50"
            >
              Shop Now
              <FaArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8 lg:py-14">
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4 items-start">
          {/* Left Column: Big Logo */}
          <div className="flex flex-col items-start justify-start">
            <div
              className="cursor-pointer transition-transform duration-300 hover:scale-105"
              onClick={() => navigate("/")}
            >
              <img
                src={footerLogoSrc}
                alt="KKisan Logo"
                className="h-28 sm:h-36 lg:h-44 w-auto object-contain drop-shadow-md"
              />
            </div>
            <p className="mt-2 text-xs sm:text-sm font-medium text-gray-300 tracking-wide">
              Powered by Kisan Daily Fresh
            </p>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h4 className="mb-3 text-xs font-bold uppercase tracking-widest text-primary-400 sm:mb-4">
              Quick Links
            </h4>
            <ul className="space-y-2.5">
              {quickLinks.map(({ label, path }) => (
                <li key={label}>
                  <button
                    onClick={() => navigate(path)}
                    className="group flex items-center gap-2 text-xs text-gray-400 transition hover:text-white sm:text-sm"
                  >
                    <span className="h-px w-3 bg-gray-700 transition-all group-hover:w-5 group-hover:bg-primary-500" />
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Contact Us */}
          <div>
            <h4 className="mb-3 text-xs font-bold uppercase tracking-widest text-primary-400 sm:mb-4">
              Contact Us
            </h4>
            <p className="mb-3 text-xs font-semibold uppercase leading-5 tracking-wide text-gray-300">
              {COMPANY_NAME}
            </p>
            <ul className="space-y-3">
              {contacts.map(({ label, href, Icon }) => (
                <li key={label} className="flex items-start gap-2">
                  <Icon
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary-400"
                    aria-hidden="true"
                  />
                  {href ? (
                    <a
                      href={href}
                      className="text-xs leading-5 text-gray-400 transition hover:text-white sm:text-sm"
                    >
                      {label}
                    </a>
                  ) : (
                    <span className="text-xs leading-5 text-gray-400 sm:text-sm">
                      {label}
                    </span>
                  )}
                </li>
              ))}
            </ul>
            <div className="mt-4 flex flex-wrap gap-2">
              <div className="flex items-center gap-1.5 rounded-lg border border-gray-700 bg-gray-800 px-2.5 py-1.5 text-xs text-gray-300">
                <FaGooglePlay className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
                Google Play
              </div>
              <div className="flex items-center gap-1.5 rounded-lg border border-gray-700 bg-gray-800 px-2.5 py-1.5 text-xs text-gray-300">
                <FaApple className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
                App Store
              </div>
            </div>
          </div>

          {/* Right Column: Connect With Us (FB, Insta, WhatsApp) */}
          <div className="flex flex-col items-start lg:items-start">
            <h4 className="mb-3 text-xs font-bold uppercase tracking-widest text-primary-400 sm:mb-4">
              Connect With Us
            </h4>
            <div className="flex flex-col gap-3 w-full max-w-xs">
              {socials.map(({ label, href, Icon, badgeBg }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-3 rounded-xl border border-gray-800 bg-gray-900/90 px-4 py-2.5 text-sm font-medium text-gray-300 transition-all duration-300 hover:border-gray-700 hover:bg-gray-800 hover:text-white hover:shadow-lg"
                >
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${badgeBg} text-white shadow-sm transition-transform duration-300 group-hover:scale-110`}
                  >
                    <Icon className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <span>{label}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
